import { useLocation, Link } from "wouter";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Home, Shield, Settings, MoreHorizontal, LogOut,
  ArrowDownToLine, ArrowUpFromLine,
  Zap, BarChart3, MessageSquare, ClipboardList, Mail,
  Inbox as InboxIcon,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFeatures, type FeatureFlags } from "@/lib/features";
import { useFeatureAwareness } from "@/lib/use-feature-awareness";
import { useActiveEntity } from "@/lib/entity-context";
import { useAuth } from "@/lib/auth";
import { useSupportMode } from "@/lib/support-mode";

interface NavItem {
  title: string;
  url: string;
  icon: any;
  testId: string;
  navKey: string;
  featureKey?: keyof FeatureFlags;
  entityScoped?: boolean;
}

const PRIMARY_ITEMS: NavItem[] = [
  { title: "nav.mobile.monitor", url: "/", icon: Home, testId: "link-home-mobile", navKey: "home" },
  { title: "nav.getPaid", url: "/ar", icon: ArrowDownToLine, testId: "link-ar-mobile", navKey: "ar", featureKey: "ar", entityScoped: true },
  { title: "nav.payBills", url: "/ap", icon: ArrowUpFromLine, testId: "link-ap-mobile", navKey: "ap", featureKey: "ap", entityScoped: true },
];

export function BottomNav() {
  const [location, setLocation] = useLocation();
  const { t } = useTranslation("common");
  const features = useFeatures();
  const { hasNew, markSeen, totalNewCount } = useFeatureAwareness();
  const { isAllEntities } = useActiveEntity();
  const { user, logout } = useAuth();

  const { data: inboxCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/inbox/count"],
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const pendingApprovalsCount = inboxCountData?.count ?? 0;

  const { active: inSupportMode } = useSupportMode();
  const isSuperAdmin = user?.role === "superadmin" && !inSupportMode;

  const filteredPrimary = PRIMARY_ITEMS.filter((item) =>
    !item.featureKey || features[item.featureKey] !== false
  );

  const isActive = (url: string) => {
    if (url === "/") return location === "/";
    return location.startsWith(url);
  };

  if (isSuperAdmin) {
    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-background border-t md:hidden z-50 pb-[env(safe-area-inset-bottom)]" data-testid="nav-bottom-admin">
        <div className="flex items-center justify-around h-16 max-w-md mx-auto">
          <Link
            href="/admin"
            className="flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-4 hover-elevate rounded-md text-foreground"
            data-testid="link-admin-mobile"
          >
            <span className="absolute -top-0 w-8 h-0.5 bg-foreground rounded-b-full" />
            <Shield className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-medium tracking-wide">Admin</span>
          </Link>
          <button
            onClick={logout}
            className="flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-4 hover-elevate rounded-md text-muted-foreground"
            data-testid="button-logout-mobile"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-medium tracking-wide">{t("nav.logout")}</span>
          </button>
        </div>
      </nav>
    );
  }

  const settingsActive = isActive("/settings");

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-background border-t md:hidden z-50 pb-[env(safe-area-inset-bottom)]" data-testid="nav-bottom">
      <div className="flex items-center justify-around h-16 max-w-md mx-auto">
        {filteredPrimary.map((item) => {
          const active = isActive(item.url);
          const muted = isAllEntities && item.entityScoped;
          return (
            <Link
              key={item.url}
              href={item.url}
              className={`flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-2 hover-elevate rounded-md ${active ? 'text-foreground' : 'text-muted-foreground'} ${muted ? 'opacity-50' : ''}`}
              data-testid={item.testId}
            >
              {active && (
                <span className="absolute -top-0 w-8 h-0.5 bg-foreground rounded-b-full" />
              )}
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[10px] font-medium tracking-wide">{t(item.title)}</span>
            </Link>
          );
        })}

        <Link
          href="/settings"
          className={`flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-2 hover-elevate rounded-md ${settingsActive ? 'text-foreground' : 'text-muted-foreground'}`}
          data-testid="link-settings-mobile"
        >
          {settingsActive && (
            <span className="absolute -top-0 w-8 h-0.5 bg-foreground rounded-b-full" />
          )}
          <Settings className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-medium tracking-wide">{t("nav.settings")}</span>
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-2 hover-elevate rounded-md text-muted-foreground"
              data-testid="button-more-menu-mobile"
              aria-label={t("nav.more")}
            >
              {pendingApprovalsCount > 0 && (
                <span
                  className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono font-semibold leading-none"
                  data-testid="badge-more-menu-count"
                >
                  {pendingApprovalsCount > 99 ? "99+" : pendingApprovalsCount}
                </span>
              )}
              {pendingApprovalsCount === 0 && totalNewCount > 0 && (
                <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-primary animate-pulse" data-testid="badge-new-features-mobile" />
              )}
              <MoreHorizontal className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[10px] font-medium tracking-wide">{t("nav.more")}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-48 mb-2 z-[60] max-h-[60vh] overflow-y-auto" data-testid="menu-more-mobile">
            <DropdownMenuItem onClick={() => setLocation("/inbox")} className="min-h-[44px]" data-testid="menu-item-inbox-mobile">
              <InboxIcon className="w-4 h-4 mr-2" />
              <span className="flex-1">{t("nav.inbox")}</span>
              {pendingApprovalsCount > 0 && (
                <span
                  className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono font-semibold leading-none"
                  data-testid="badge-pending-approvals-mobile"
                >
                  {pendingApprovalsCount > 99 ? "99+" : pendingApprovalsCount}
                </span>
              )}
            </DropdownMenuItem>
            {features.automations !== false && (
              <DropdownMenuItem onClick={() => { markSeen("automations"); setLocation("/automations"); }} className="min-h-[44px]" data-testid="menu-item-automations-mobile">
                <Zap className="w-4 h-4 mr-2" />
                <span className="flex-1">{t("nav.automations")}</span>
                {hasNew("automations") && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse" data-testid="badge-new-automations-mobile" />
                )}
              </DropdownMenuItem>
            )}
            {features.aiReports !== false && (
              <DropdownMenuItem onClick={() => { markSeen("aiReports"); setLocation("/reports"); }} className="min-h-[44px]" data-testid="menu-item-reports-mobile">
                <BarChart3 className="w-4 h-4 mr-2" />
                <span className="flex-1">{t("nav.reports")}</span>
                {hasNew("aiReports") && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse" data-testid="badge-new-reports-mobile" />
                )}
              </DropdownMenuItem>
            )}
            {features.aiChat !== false && (
              <DropdownMenuItem onClick={() => setLocation("/chat")} className="min-h-[44px]" data-testid="menu-item-chat-mobile">
                <MessageSquare className="w-4 h-4 mr-2" />
                {t("nav.chat")}
              </DropdownMenuItem>
            )}
            {features.tasks !== false && (
              <DropdownMenuItem onClick={() => setLocation("/tasks")} className="min-h-[44px]" data-testid="menu-item-tasks-mobile">
                <ClipboardList className="w-4 h-4 mr-2" />
                {t("nav.tasks")}
              </DropdownMenuItem>
            )}
            {features.communications !== false && (
              <DropdownMenuItem onClick={() => { markSeen("communications"); setLocation("/communications"); }} className="min-h-[44px]" data-testid="menu-item-communications-mobile">
                <Mail className="w-4 h-4 mr-2" />
                <span className="flex-1">{t("nav.communications")}</span>
                {hasNew("communications") && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse" data-testid="badge-new-communications-mobile" />
                )}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
